import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, HttpStatusCode, InternalAxiosRequestConfig} from "axios";
import {AuthenticationRepository} from "./AuthenticationRepository.ts";
import {firstValueFrom} from "rxjs";
import HTTPConverter from "./converters/HTTPConverter.ts";
import generalErrorConverter from "./converters/GeneralErrorConverter.ts";
import {B2BAgenciesResponse} from "../domain/entities/B2BAgency.ts";
import {B2BDashboardParams, B2BDashboardResponse} from "../domain/entities/B2BDashboard.ts";
import {B2BAgencyStatsResponse, B2BClientTripbooksResponse} from "../domain/entities/B2BAgencyStats.ts";
import {B2BAgencyActivityResponse, B2BPackDetailResponse} from "../domain/entities/B2BPackDetail.ts";
import {B2BReferralStatsResponse} from "../domain/entities/B2BReferralStats.ts";
import {SetTripbookFeedbackPayload, TripbookFeedback} from "../domain/entities/TripbookFeedback.ts";
import {
    AdminCodeHistoryResponse,
    AdminCodesFilterParams,
    AdminCodesResponse,
    AdminCodesSummaryResponse,
    AdminCodeType,
    AdminTourOperatorFilter
} from "../domain/entities/AdminCode.ts";

export class B2BMetricsRepository {

    client: AxiosInstance

    get serverBaseURL(): string {
        return import.meta.env.VITE_APP_SERVER_URL
    }

    constructor(authenticationRepository: AuthenticationRepository) {
        this.client = axios.create({
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            timeout: 20000,
            validateStatus: (status: number) => status >= 200 && status < 300
        })

        this.client.interceptors.request.use(
            async (
                config: InternalAxiosRequestConfig
            ): Promise<InternalAxiosRequestConfig & { retry: number }> => {
                const token = await firstValueFrom(authenticationRepository.getIDToken())
                config.headers.set('Authorization', `Bearer ${token}`)

                const configWithRetry = config as InternalAxiosRequestConfig & {
                    retry: number
                }
                return { ...config, retry: configWithRetry.retry === 0 ? 0 : 1 }
            }
        )

        this.client.interceptors.response.use(
            (response) => HTTPConverter.fromResponse(response),
            async (err: AxiosError) => {
                const config = err.config as AxiosRequestConfig & { retry: number }
                if (err?.response?.status === HttpStatusCode.Unauthorized && config && config.retry > 0) {
                    config.retry = 0
                    return this.client.request(config)
                }
                return generalErrorConverter.fromBackendServerError(err)
            }
        )
    }

    getB2BAgencies(): Promise<B2BAgenciesResponse> {
        return this.client.get(`${this.serverBaseURL}/metrics/b2b/agencies`)
    }

    getB2BDashboard(params: B2BDashboardParams): Promise<B2BDashboardResponse> {
        const query = new URLSearchParams({range: params.range, currency: params.currency})
        if (params.range === 'CUSTOM') {
            if (params.from) query.set('from', params.from)
            if (params.to) query.set('to', params.to)
        }
        return this.client.get(`${this.serverBaseURL}/metrics/b2b/dashboard?${query.toString()}`)
    }

    getB2BAgencyStats(agencyId: string, isSelfService = false): Promise<B2BAgencyStatsResponse> {
        const path = isSelfService
            ? `${this.serverBaseURL}/metrics/b2b/me/stats`
            : `${this.serverBaseURL}/metrics/b2b/agencies/${agencyId}/stats`
        return this.client.get(path)
    }

    getB2BPackDetail(agencyId: string, packId: string, isSelfService = false): Promise<B2BPackDetailResponse> {
        const path = isSelfService
            ? `${this.serverBaseURL}/metrics/b2b/me/packs/${packId}`
            : `${this.serverBaseURL}/metrics/b2b/agencies/${agencyId}/packs/${packId}`
        return this.client.get(path)
    }

    getB2BAgencyTripbooks(agencyId: string, limit: number, offset: number, isSelfService = false): Promise<B2BClientTripbooksResponse> {
        const path = isSelfService
            ? `${this.serverBaseURL}/metrics/b2b/me/tripbooks`
            : `${this.serverBaseURL}/metrics/b2b/agencies/${agencyId}/tripbooks`
        return this.client.get(path, {params: {limit, offset}})
    }

    getB2BAgencyActivity(agencyId: string, market?: string, isSelfService = false): Promise<B2BAgencyActivityResponse> {
        const path = isSelfService
            ? `${this.serverBaseURL}/metrics/b2b/me/activity`
            : `${this.serverBaseURL}/metrics/b2b/agencies/${agencyId}/activity`
        return this.client.get(path, {params: {market}})
    }

    getB2BReferralStats(agencyId: string, year?: number, isSelfService = false): Promise<B2BReferralStatsResponse> {
        const path = isSelfService
            ? `${this.serverBaseURL}/metrics/b2b/me/referrals`
            : `${this.serverBaseURL}/metrics/b2b/agencies/${agencyId}/referrals`
        return this.client.get(path, {params: {year}})
    }

    // Staff-only — writes through to 51trips-ms-tripbook (live, not the BigQuery ETL).
    setTripbookFeedback(tripId: string, tripbookId: string, payload: SetTripbookFeedbackPayload): Promise<TripbookFeedback> {
        return this.client.put(`${this.serverBaseURL}/metrics/b2b/trips/${tripId}/tripbook/${tripbookId}/feedback`, payload)
    }

    deleteTripbookFeedback(tripId: string, tripbookId: string): Promise<void> {
        return this.client.delete(`${this.serverBaseURL}/metrics/b2b/trips/${tripId}/tripbook/${tripbookId}/feedback`)
    }

    // Admin-only support screen — monitors invitation codes / TripBooks platform-wide.
    getAdminCodes(filter: AdminCodesFilterParams): Promise<AdminCodesResponse> {
        const params = new URLSearchParams()
        filter.agencyIds?.forEach((id) => params.append('agencyId', id))
        if (filter.tourOperator) params.set('tourOperator', filter.tourOperator)
        if (filter.type) params.set('type', filter.type)
        filter.steps?.forEach((step) => params.append('step', step))
        if (filter.incidentOnly) params.set('incident', 'true')
        if (filter.from) params.set('from', filter.from)
        if (filter.to) params.set('to', filter.to)
        if (filter.q) params.set('q', filter.q)
        params.set('page', String(filter.page))
        params.set('size', String(filter.size))
        return this.client.get(`${this.serverBaseURL}/metrics/admin/codes?${params.toString()}`)
    }

    getAdminCodesSummary(
        agencyIds: string[],
        tourOperator: AdminTourOperatorFilter,
        type: AdminCodeType,
    ): Promise<AdminCodesSummaryResponse> {
        const params = new URLSearchParams()
        agencyIds.forEach((id) => params.append('agencyId', id))
        params.set('tourOperator', tourOperator)
        params.set('type', type)
        return this.client.get(`${this.serverBaseURL}/metrics/admin/codes/summary?${params.toString()}`)
    }

    getAdminCodeHistory(codeId: string, orderId?: string | null): Promise<AdminCodeHistoryResponse> {
        const params = new URLSearchParams()
        if (orderId) params.set('orderId', orderId)
        const query = params.toString()
        return this.client.get(
            `${this.serverBaseURL}/metrics/admin/codes/${codeId}/history${query ? `?${query}` : ''}`,
        )
    }
}
